/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import { forwardRef, Fragment, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';

// Component imports
import { Button, Checkbox, Col, Collapse, Form, Input, Row, Select, Space, Spin, Tooltip, Typography, Upload } from 'antd';
import { CloseOutlined, UploadOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import CustomInputNumber from './Fields/CustomInputNumber.jsx';
import FormSectionHeader from '../UI/FormSectionHeader';
import WitProviderForm from './NlpPipelineProviderForms/WitProviderForm.jsx';
import HelviaNLPSpecificationProviderForm from './NlpPipelineProviderForms/HelviaNLPSpecificationProviderForm.jsx';

// Misc imports
import { LANGUAGE_OPTIONS, NLP_TYPES, NLP_PIPELINE_STATUSES } from '../../constants/main';
import { useCreateNlpPipelineMutation, useDeleteNlpPipelineMutation, useFetchNlpPipelineQuery, useLazyFetchHelviaPipelineSettingsQuery, useUpdateHelviaPipelineSettingsMutation, useUpdateNlpPipelineMutation } from '../../store/nlpPipelines/nlpPipelinesApi.js';
import { useBotLanguage } from '../../utils/hooks/useBotLanguage';
import { DialogflowProviderForm, DialogflowProviderConfigurationForm } from './NlpPipelineProviderForms/DialogflowProviderForm.jsx';
import { LuisProviderForm, LuisProviderConfigurationForm } from './NlpPipelineProviderForms/LuisProviderForm.jsx';
import { HelviaRAGPipelineProviderForm, HelviaRAGPipelineProviderConfigurationForm } from './NlpPipelineProviderForms/HelviaRAGPipelineProviderForm.jsx';
import { OpenAiProviderForm, OpenAiProviderConfigurationForm } from './NlpPipelineProviderForms/OpenAiProviderForm.jsx';
import { HelviaGPTPipelineProviderConfigurationForm, HelviaGPTPipelineProviderForm } from './NlpPipelineProviderForms/HelviaGPTPipelineProviderForm.jsx';
import { useModalConfirm } from '../../utils/hooks/useModalConfirm';
import useTheme from '../../utils/hooks/useTheme/index.js';
import { isEmptyArray } from '../../utils/is/isEmptyArray/index.js';
import openConsoleAlertMessage from '../../utils/misc/openConsoleAlertMessage';

// Constant declarations
const { Option } = Select;
const { Text } = Typography;

// Extract and discard unused backend properties
const filterFormValues = (values) => {
  const { createdAt, lastTrainedAt, status, updatedAt, failedReason, ...filteredValues } = values;
  return filteredValues;
};

const NlpPipelineTagsFormItem = ({
  label='',
  placeholder='',
  itemName='',
  formProps={},
  initialValues={},
  description=''
}) => {
  return <Form.Item
    name={itemName}
    labelCol={{ xs: formProps.mainColSpan }}
    wrapperCol={{ xs: formProps.secondaryColSpan }}
    className="mb-2"
    label={<Fragment>
      <Text>{label}</Text>
      <Tooltip title={description}>
        <QuestionCircleOutlined className="ml-3 fs-md text-secondary" />
      </Tooltip>
    </Fragment>}
  >
    <Select
      mode="tags"
      size="large"
      tokenSeparators={[',']}
      placeholder={placeholder}
      options={
        initialValues?.tags?.length > 0 ? initialValues?.tags.map((tag)=> {
          return {
            value: tag,
            label: tag
          };
        }) : null
      }
      maxTagPlaceholder={(omittedValues) => (
        <Tooltip
          title={
            <div
              className="overflow-y-auto overflow-x-hidden scrollbar-custom"
              style={{ maxHeight: 280, maxWidth: 220 }}
            >
              {
                omittedValues.map((value, index) => (
                  <Text.Paragraph ellipsis key={index} className="fs-base text-muted mb-0">
                    {value.label}

                    <Button
                      size="small"
                      type="link"
                      className="mb-1"
                      icon={<CloseOutlined className="text-secondary mr-0 fs-sm"/>}
                    />
                  </Text.Paragraph>
                ))
              }
            </div>
          }
          placement="bottomLeft"
        >
+{omittedValues.length}
        </Tooltip>
      )}
    />
  </Form.Item>;
};
NlpPipelineTagsFormItem.propTypes = {
  label: PropTypes.string,
  placeholder: PropTypes.string,
  itemName: PropTypes.string,
  formProps: PropTypes.object,
  initialValues: PropTypes.object,
  description: PropTypes.string
};

const NlpPipelineForm = forwardRef(({
  id,
  isUpdateMode = false,
  onSuccess,
  onCancel,
  setHasPendingChanges = (hasPendingChanges = false) => {},
  disableConfirm = false,
  setPreventModalConfirm,
  bodyClassName,
  footerClassName
}, ref) => {
  const [helviaPipelineSettingsForm] = Form.useForm();
  const [form] = Form.useForm();
  const { orgId, tenantId } = useParams();
  const { t } = useTranslation();
  const { botLanguages, primaryBotLanguage } = useBotLanguage();
  const { formProps } = useTheme();
  const ragPipelineSettingsFormRef = useRef(null);

  const { data: nlpPipeline, isFetching: isFetchingNlpPipeline } = useFetchNlpPipelineQuery({ orgId, tenantId, nlpPipelineId: id }, { skip: !isUpdateMode || !id });
  const [ fetchHelviaPipelineSettings, { isLoading: isFetchingHelviaPipelineSettings } ] = useLazyFetchHelviaPipelineSettingsQuery();
  const [ updateHelviaPipelineSettings ] = useUpdateHelviaPipelineSettingsMutation();
  const [ updateNlpPipeline, { isLoading: isUpdatingNlpPipeline }] = useUpdateNlpPipelineMutation();
  const [createNlpPipeline, { isLoading: isCreatingNlpPipeline }] = useCreateNlpPipelineMutation();
  const [, { isLoading: isDeletingNlpPipeline }] = useDeleteNlpPipelineMutation({ fixedCacheKey: 'delete-nlp-pipeline-shared' });

  const { showModalConfirm } = useModalConfirm(!disableConfirm && (!isUpdatingNlpPipeline && !isCreatingNlpPipeline), !disableConfirm && (!isUpdatingNlpPipeline && !isCreatingNlpPipeline));

  const [ helviaPipelineSettings, setHelviaPipelineSettings ] = useState();
  const [, updateState] = useState(); // Hook to force a component re-render

  const initialValues = useMemo(() => {
    const _filteredValues = id && nlpPipeline?.id === id ? filterFormValues(nlpPipeline) : {};
    _filteredValues.enableTrainingTags = !!_filteredValues.includeTrainingTags?.length || !!_filteredValues.excludeTrainingTags?.length;
    return _filteredValues;
  }, [id, nlpPipeline]);

  // Added so that the parent component can call the form's submit method
  useImperativeHandle(ref, () => ({
    form,
    onSubmit
  }));

  useEffect(() => {
    form.resetFields();
    helviaPipelineSettingsForm.resetFields();

    return () => {
      form.resetFields();
    };
  }, [id, nlpPipeline?.id]);

  useEffect(() => {
    helviaPipelineSettingsForm.resetFields();
  }, [helviaPipelineSettings]);

  useEffect(() => {
    if (form.getFieldValue('type') === nlpPipeline?.type) {
      getHelviaPipelineSettings();
    } else {
      setHelviaPipelineSettings(null);
    }
  }, [form.getFieldValue('type')]);

  useEffect(() => {
    if( nlpPipeline?.type === NLP_TYPES.HELVIA_RAG_PIPELINE || nlpPipeline?.type === NLP_TYPES.HELVIA_GPT) {
      getHelviaPipelineSettings();
    }
  }, [nlpPipeline]);

  const handleShowModalConfirm = (action) => {
    return (values) => {
      showModalConfirm({
        title: t('Are you sure you want to apply the changes to the NLP system?'),
        content: t('Changing the NLP system settings can affect the agent\'s performance.'),
        okText: t('Apply changes'),
        cancelText: t('Cancel'),
        onConfirm: () => {
          action(values);
        }
      });
    };
  };

  const transformGlossaries = (providers) => {
    return providers?.map((provider) => {
      const remoteGlossaries = provider?.remote_glossaries || null;
      return {
        ...provider,
        remote_glossaries: remoteGlossaries?.reduce((acc, entry) => {
          return { ...acc, [entry.key]: { uri: entry.value } };
        }, {})
      };
    });
  };

  const submitHelviaPipelineSettings = async (type) => {
    if (helviaPipelineSettings) {
      let data = helviaPipelineSettingsForm.getFieldsValue();

      if (type === NLP_TYPES.HELVIA_RAG_PIPELINE) {
        if (data?.configuration) {
          // Ensure 'enabled' flags are preserved
          Object.keys(data.configuration).forEach((section) => {
            if (data.configuration[section]?.enabled === undefined) {
              data.configuration[section].enabled = helviaPipelineSettings.configuration[section]?.enabled ?? true;
            }
          });

          Object.entries(data.configuration).forEach(([key, value]) => {
            if (value?.enabled === false && value?.providers?.length > 0) {
              let newProviders = [];
              value.providers.forEach((provider) => {
                const shouldBeRemoved = Object.entries(provider)
                  .filter(([key, _]) => key !== 'seed')
                  .some(([_, value]) => !value);
                if (!shouldBeRemoved) {
                  newProviders.push(provider);
                }
              });
              data.configuration[key].providers = newProviders;
            }
          });
        }

        if (data.configuration?.translation) {
          data = {
            configuration: {
              ...data.configuration,
              translation: {
                ...data.configuration.translation,
                hide_urls: data.configuration?.translation ? data.configuration?.translation?.hide_urls : helviaPipelineSettings.configuration?.translation?.hide_urls,
                corpus_translation_providers: data.configuration?.translation ? transformGlossaries(data.configuration?.translation?.corpus_translation_providers) : helviaPipelineSettings.configuration.translation.corpus_translation_providers,
                query_translation_providers: data.configuration?.translation ? transformGlossaries(data.configuration?.translation?.query_translation_providers) : helviaPipelineSettings.configuration.translation.query_translation_providers,
                response_translation_providers: data.configuration?.translation ? transformGlossaries(data.configuration?.translation?.response_translation_providers) : helviaPipelineSettings.configuration.translation.response_translation_providers
              }
            }
          };
        }
      }

      const result = await updateHelviaPipelineSettings({
        serviceUrl: form.getFieldValue('serviceUrl'),
        pipelineId: form.getFieldValue('pipelineId'),
        token: form.getFieldValue('bearerToken'),
        data: data,
        successMessage: { showMessage: false }
      });

      if (result) {
        helviaPipelineSettingsForm.setFieldsValue(result?.data);
      }
    }
  };

  const onSubmit = async (values) => {
    const { enableTrainingTags, ..._values } = values;

    if (nlpPipeline && isUpdateMode) {
      // Update existing pipeline
      const { language, projectId, ...valuesToSubmit } = _values;

      if (helviaPipelineSettings && (valuesToSubmit?.type === NLP_TYPES.HELVIA_GPT || valuesToSubmit?.type === NLP_TYPES.HELVIA_RAG_PIPELINE)) {
        try {
          // Validate fields and wait for the result
          await helviaPipelineSettingsForm.validateFields();
        } catch (errorInfo) {
          if (!isEmptyArray(errorInfo?.errorFields)) {
            const { errorFields } = errorInfo;
            ragPipelineSettingsFormRef?.current?.navigateToErrorField(errorFields?.[0]?.name);
            return; // Stop execution if there are errors
          }
        }
      }

      await updateNlpPipeline({ orgId, tenantId, nlpPipelineId: nlpPipeline.id, data: valuesToSubmit });
      setHasPendingChanges(false);

      if (helviaPipelineSettings && (valuesToSubmit?.type === NLP_TYPES.HELVIA_GPT || valuesToSubmit?.type === NLP_TYPES.HELVIA_RAG_PIPELINE)) {
        submitHelviaPipelineSettings(valuesToSubmit?.type);
      }
    } else {
      // Create
      // Note: Upon creating an NLP pipeline the status will be 'CREATED'
      const { ...valuesToSubmit } = _values;

      if (helviaPipelineSettings && (valuesToSubmit?.type === NLP_TYPES.HELVIA_GPT || valuesToSubmit?.type === NLP_TYPES.HELVIA_RAG_PIPELINE)) {
        try {
          // Validate fields and wait for the result
          await helviaPipelineSettingsForm.validateFields();
        } catch (errorInfo) {
          if (!isEmptyArray(errorInfo?.errorFields)) {
            const { errorFields } = errorInfo;
            ragPipelineSettingsFormRef?.current?.navigateToErrorField(errorFields?.[0]?.name);
            return; // Stop execution if there are errors
          }
        }
      }

      const createdPipeline = await createNlpPipeline({ orgId, tenantId, data: _values });

      if (helviaPipelineSettings && (valuesToSubmit?.type === NLP_TYPES.HELVIA_GPT || valuesToSubmit?.type === NLP_TYPES.HELVIA_RAG_PIPELINE)) {
        submitHelviaPipelineSettings(valuesToSubmit?.type);
      }

      if (createdPipeline.data) {
        setHasPendingChanges(false);
        // Clear form
        form.resetFields();
        onSuccess();
      }
    }

    setPreventModalConfirm(false);
  };

  const getHelviaPipelineSettings = async () => {
    try {
      const serviceUrl = form.getFieldValue('serviceUrl');
      const pipelineId = form.getFieldValue('pipelineId');
      const token = form.getFieldValue('bearerToken');
      if (serviceUrl && pipelineId && token) {
        const result = await fetchHelviaPipelineSettings({ serviceUrl: serviceUrl, pipelineId: pipelineId, token: token }).unwrap();

        setHelviaPipelineSettings(result);
      }
    } catch (e) {
      setHelviaPipelineSettings(null);
    }
  };

  const renderProviderForm = () => {
    switch (form.getFieldValue('type')) {
      case NLP_TYPES.OPENAI_NLP:
        return <OpenAiProviderForm />;
      case NLP_TYPES.DIALOGFLOW_NLP:
        return <DialogflowProviderForm nlpPipeline={nlpPipeline}/>;
      case NLP_TYPES.LUIS_NLP:
        return <LuisProviderForm />;
      case NLP_TYPES.WIT:
        return <WitProviderForm />;
      case NLP_TYPES.HELVIA_GPT:
        return <HelviaGPTPipelineProviderForm />;
      case NLP_TYPES.HELVIA_RAG_PIPELINE:
        return <HelviaRAGPipelineProviderForm />;
      case NLP_TYPES.HELVIA_NLP_SPECIFICATION:
        return <HelviaNLPSpecificationProviderForm />;
      default:
        return null;
    }
  };

  const renderNlpConfigurationForm = () => {
    switch (form.getFieldValue('type') || nlpPipeline?.type) {
      case NLP_TYPES.OPENAI_NLP:
        return <OpenAiProviderConfigurationForm form={form}/>;
      case NLP_TYPES.DIALOGFLOW_NLP:
        return <DialogflowProviderConfigurationForm />;
      case NLP_TYPES.LUIS_NLP:
        return <LuisProviderConfigurationForm />;
      case NLP_TYPES.WIT:
        return null;
      case NLP_TYPES.HELVIA_GPT:
        return (
          <HelviaGPTPipelineProviderConfigurationForm
            gptPipelineSettings={helviaPipelineSettings}
            gptPipelineSettingsForm={helviaPipelineSettingsForm}
            isLoading={isFetchingHelviaPipelineSettings}
            onSubmit={onSubmit}
            setHasPendingChanges={setHasPendingChanges}
            updateState={updateState}
          />
        );
      case NLP_TYPES.HELVIA_RAG_PIPELINE:
        return (
          <HelviaRAGPipelineProviderConfigurationForm
            ref={ragPipelineSettingsFormRef}
            ragPipelineSettings={helviaPipelineSettings}
            ragPipelineSettingsForm={helviaPipelineSettingsForm}
            isLoading={isFetchingHelviaPipelineSettings}
            onSubmit={onSubmit}
            setHasPendingChanges={setHasPendingChanges}
            updateState={updateState}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Fragment>
      <Row className={bodyClassName}>
        <Col span={24}>
          <Form
            form={form}
            validateMessages={{
              required: t('Please enter a value'),
              whitespace: t('Value cannot be blank')
            }}
            initialValues={initialValues}
            validateTrigger={['onBlur', 'onChange']}
            onFinish={isUpdateMode && !disableConfirm ? handleShowModalConfirm(onSubmit) : onSubmit}
            onValuesChange={(_, allFields) => {
              setHasPendingChanges(true);
              updateState(allFields);
            }}
            labelCol={{ xs: formProps.mainColSpan }}
            wrapperCol={{ xs: formProps.secondaryColSpan }}
            labelAlign="left"
          >
            {
              !form.getFieldValue('name') && (isFetchingNlpPipeline) ? (
                <Row align="middle" justify="center">
                  <Spin className="h-100" />
                </Row>
              ) : (
                <Fragment>
                  <Form.Item
                    name="type"
                    label={t('Select Provider')}
                    rules={[{ required: true }]}
                  >
                    <Select
                      className="w-100"
                      placeholder={t('Choose a provider')}
                      disabled={isUpdateMode || nlpPipeline?.status === NLP_PIPELINE_STATUSES.SYNCING || nlpPipeline?.status === NLP_PIPELINE_STATUSES.TRAINING}
                      getPopupContainer={triggerNode => triggerNode.parentElement} // Fix drop-menu position issues by rendering it in the parent element
                    >
                      {
                        Object.entries(NLP_TYPES)
                          .filter(provider => provider[1] !== NLP_TYPES.LEX && provider[1] !== NLP_TYPES.WIT) // Hide Lex and Wit options
                          .map(([key, value]) => (
                            <Option key={key} value={value}>{t(value, { context: 'nlp provider' })}</Option>
                          ))
                      }
                    </Select>
                  </Form.Item>

                  <Form.Item
                    name="name"
                    label={t('Name')}
                    rules={[{ required: true, type: 'string', whitespace: true }]}
                  >
                    <Input
                      placeholder={t('Enter a name')}
                    />
                  </Form.Item>

                  <Form.Item
                    name="description"
                    label={t('Description')}
                  >
                    <Input.TextArea
                      placeholder={t('Enter a short description')}
                      autoSize={{ minRows: 4, maxRows: 7 }}
                    />
                  </Form.Item>

                  <Form.Item
                    name="language"
                    label={t('Language')}
                    rules={[{ required: true }]}
                  >
                    <Select
                      placeholder={t('Choose a language')}
                      dropdownMatchSelectWidth={false}
                      allowClear
                      disabled={nlpPipeline}
                      getPopupContainer={triggerNode => triggerNode.parentElement}
                      showSearch
                      filterOption={(inputValue, option) => option.searchValue.toLowerCase()?.includes(inputValue.toLowerCase())}
                      options={botLanguages
                        .map(language => {
                          const currentLang = LANGUAGE_OPTIONS.find(lang => lang.key === language);
                          if(!currentLang) {
                            return {};
                          }
                          return {
                            label: <Space size={4}>{currentLang?.icon} {currentLang.label} {currentLang.key === primaryBotLanguage ? <Text type="secondary">{t('(primary)')}</Text> : null}</Space>,
                            value: currentLang.key,
                            searchValue: currentLang.label
                          };
                        })
                      }
                    />
                  </Form.Item>

                  <Form.Item
                    name="secondaryLanguages"
                    label={t('Secondary Languages')}
                  >
                    <Select
                      mode="multiple"
                      placeholder={t('Choose secondary languages')}
                      dropdownMatchSelectWidth={false}
                      disabled={!form.getFieldValue('language') && !nlpPipeline?.language}
                      filterOption={(inputValue, option) => option.searchValue?.includes(inputValue.toLowerCase())}
                      options={[
                        {
                          label: 'Training Language',
                          options: [
                            {
                              label: (
                                <Space size={5}>
                                  {LANGUAGE_OPTIONS.find((language) => language.key === form.getFieldValue('language') || language.key === nlpPipeline?.language)?.label}
                                  {form.getFieldValue('language') === primaryBotLanguage ? <Text type="secondary">{t('(primary)')}</Text> : null}
                                </Space>
                              ),
                              searchValue: LANGUAGE_OPTIONS.find((language) => language.key === form.getFieldValue('language') || language.key === nlpPipeline?.language)?.label.toLowerCase()
                            }
                          ]
                        },
                        {
                          label: 'Other Languages',
                          options: LANGUAGE_OPTIONS.filter((language) => botLanguages.includes(language.key) && (form.getFieldValue('language') !== language.key && nlpPipeline?.language !== language.key)).map(lang => ({
                            value: lang.key,
                            label: (
                              <Space size={5}>
                                {lang.label}
                              </Space>
                            ),
                            searchValue: lang.label.toLowerCase()
                          }))
                        }
                      ]}
                      getPopupContainer={triggerNode => triggerNode.parentElement}
                    />
                  </Form.Item>

                  { (form.getFieldValue('type') || nlpPipeline?.type) && form.getFieldValue('type') === NLP_TYPES.HELVIA_RAG_PIPELINE ? (
                    <Fragment>
                      <Form.Item
                        name={['settings', 'includeHistory']}
                        valuePropName="checked"
                        label={<Fragment>
                          <Text>{t('Include Session History')}</Text>
                          <Tooltip title={t('The LLM will include the session history when generating the response.')}>
                            <QuestionCircleOutlined className="ml-3 fs-md text-secondary" />
                          </Tooltip>
                        </Fragment>}
                      >
                        <Checkbox />
                      </Form.Item>
                      <Form.Item
                        name={['settings', 'maxHistoryTurns']}
                        label={<Fragment>
                          <Text>{t('Maximum History Turns')}</Text>
                          <Tooltip title={t('A turn is a group of messages starting with one or more consecutive user messages followed by any agent responses until the next user message.')}>
                            <QuestionCircleOutlined className="ml-2 fs-md text-secondary" />
                          </Tooltip>
                        </Fragment>}
                        validateTrigger={['onChange', 'onBlur']}
                      >
                        <CustomInputNumber
                          disabled={!form.getFieldValue(['settings', 'includeHistory'])}
                          className="w-10"
                          type="number"
                          min={1}
                          max={30} />
                      </Form.Item>
                    </Fragment>)
                    : null }

                  {/* TAGS */}
                  <Form.Item
                    name={['enableTrainingTags']}
                    valuePropName="checked"
                    label={<Fragment>
                      <Text>{t('Use Training Tags')}</Text>
                      <Tooltip title={t('Train NLP on a specific subset of content based on tags.')}>
                        <QuestionCircleOutlined className="ml-3 fs-md text-secondary" />
                      </Tooltip>
                    </Fragment>}
                    className="mb-2"
                  >
                    <Checkbox />
                  </Form.Item>

                  <Form.Item
                    shouldUpdate={(prevValues, currentValues) => prevValues?.enableTrainingTags !== currentValues?.enableTrainingTags}
                    wrapperCol={24}
                  >
                    {({ getFieldValue }) => {
                      const enabled = getFieldValue(['enableTrainingTags']);
                      return (
                        enabled ? (
                          <Fragment>
                            {/* Included Training Tags */}
                            <NlpPipelineTagsFormItem
                              label={t('Included Tags')}
                              placeholder={t('Add included tags for the training process')}
                              itemName={'includeTrainingTags'}
                              formProps={formProps}
                              initialValues={initialValues}
                              description={t('Only Automated Answers that contain at least one of these tags will be included in the training dataset.')}
                            />
                            {/* ./Included Training Tags */}
                            {/* Excluded Training Tags */}
                            <NlpPipelineTagsFormItem
                              label={t('Excluded Tags')}
                              placeholder={t('Add excluded tags for the training process')}
                              itemName={'excludeTrainingTags'}
                              formProps={formProps}
                              initialValues={initialValues}
                              description={t('Any activity containing one or more of these tags will be excluded from training.')}
                            />
                            {/* ./ Excluded Training Tags */}
                          </Fragment>
                        ) : null
                      );
                    }}
                  </Form.Item>
                  {/* ./ TAGS */}

                  <Form.Item
                    shouldUpdate
                    noStyle
                  >
                    {() => (
                      renderProviderForm()
                    )}
                  </Form.Item>

                  <Form.Item
                    shouldUpdate
                    noStyle
                  >
                    <Fragment>
                      {
                        (form.getFieldValue('type') || nlpPipeline?.type) && form.getFieldValue('type') !== NLP_TYPES.HELVIA_NLP_SPECIFICATION ? (
                          <Collapse defaultActiveKey={['1']} ghost>
                            <Collapse.Panel
                              className={form.getFieldValue('type') === NLP_TYPES.HELVIA_RAG_PIPELINE && 'rag-pipeline-collapse'}
                              showArrow={false}
                              header={
                                <Row
                                  onClick={async () => {
                                    if (form.getFieldValue('type') === NLP_TYPES.HELVIA_RAG_PIPELINE || form.getFieldValue('type') === NLP_TYPES.HELVIA_GPT) {
                                      await getHelviaPipelineSettings();
                                    }
                                  }}
                                >
                                  <FormSectionHeader
                                    title={t('NLP Configuration')}
                                  />
                                </Row>
                              }
                              key="1"
                            >
                              {renderNlpConfigurationForm()}
                            </Collapse.Panel>
                          </Collapse>
                        ) : null
                      }
                    </Fragment>
                  </Form.Item>
                </Fragment>
              )
            }
          </Form>
        </Col>
      </Row>

      <div className={footerClassName}>
        <Row justify="end" gutter={8}>
          <Col>
            {
              nlpPipeline?.type === NLP_TYPES.DIALOGFLOW_NLP || form.getFieldValue('type') === NLP_TYPES.DIALOGFLOW_NLP ? (
                <Upload
                  accept=".json"
                  showUploadList={false}
                  beforeUpload={file => {
                    const reader = new FileReader();
                    reader.onload = e => {
                      let jsonObj = JSON.parse(e.target.result);
                      let validationKeys = ['project_id', 'private_key_id', 'private_key', 'client_email', 'client_id', 'token_uri'];
                      let validationKeysFound = [];
                      for (const [key] of Object.entries(jsonObj)) {
                        if (validationKeys.includes(key)) {
                          validationKeysFound.push(key);
                        }
                      }
                      if (validationKeys.length !== validationKeysFound.length) {
                        openConsoleAlertMessage({ type: 'error', message: t('The configuration file of the NLP model is wrong.') });
                      } else {
                        const {
                          type: trainingType,
                          project_id: projectId,
                          private_key: privateKey,
                          private_key_id: privateKeyId,
                          client_email: clientEmail,
                          client_id: clientId,
                          token_uri: tokenServerUri,
                          ...rest
                        } = jsonObj;

                        form.setFieldsValue({
                          trainingType,
                          projectId,
                          privateKey,
                          privateKeyId,
                          clientEmail,
                          clientId,
                          tokenServerUri,
                          ...rest
                        });
                      }
                    };
                    reader.readAsText(file);
                    return false;
                  }}
                >
                  <Button
                    icon={<UploadOutlined />}
                    size="large"
                  >
                    {t('Load from JSON')}
                  </Button>
                </Upload>
              ) : null
            }
          </Col>

          <Col>
            <Button
              size="large"
              onClick={onCancel}
              disabled={isUpdatingNlpPipeline || isCreatingNlpPipeline}
            >
              {t('Cancel')}
            </Button>
          </Col>

          <Col>
            <Tooltip title={nlpPipeline?.status === NLP_PIPELINE_STATUSES.SYNCING || nlpPipeline?.status === NLP_PIPELINE_STATUSES.TRAINING ? t('The pipeline is syncing or training') : ''}>
              <Button
                type="primary"
                disabled={!form.getFieldValue('type') || isDeletingNlpPipeline}
                loading={isUpdatingNlpPipeline || isCreatingNlpPipeline || nlpPipeline?.status === NLP_PIPELINE_STATUSES.SYNCING || nlpPipeline?.status === NLP_PIPELINE_STATUSES.TRAINING}
                size="large"
                onClick={form.submit}
              >
                {isUpdateMode ? 'Save Changes' : 'Create Pipeline'}
              </Button>
            </Tooltip>
          </Col>
        </Row>
      </div>
    </Fragment>
  );
});

NlpPipelineForm.propTypes = {
  id: PropTypes.string,
  isUpdateMode: PropTypes.bool,
  onSuccess: PropTypes.func,
  onCancel: PropTypes.func,
  setHasPendingChanges: PropTypes.func,
  disableConfirm: PropTypes.bool,
  setPreventModalConfirm: PropTypes.func,
  bodyClassName: PropTypes.string,
  footerClassName: PropTypes.string
};

export default NlpPipelineForm;
